import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthState } from 'src/shared/store/state/auth.state';

import { IGroup } from '../../interfaces/groups/group.interface';
import { IQuestionChoices } from '../../interfaces/radio-checkbox-config.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { GroupsService } from '../../services/groups.service';
import {
    CreateNewGroup,
    DeleteCachedGroups,
    DeleteGroup,
    DeleteGroupToEdit,
    DisableGroup,
    EnableGroup,
    GetAllGroups,
    GetGroupById,
    PatchGroup,
} from '../actions/groups.action';
import { SetRepertoire } from '../actions/repertoire.action';

export interface GroupsStateModel {
    groups: IGroup[];
    groupToEdit: IGroup;
}

@State<GroupsStateModel>({
    name: 'groups',
    defaults: {
        groups: [],
        groupToEdit: null,
    },
})
@Injectable()
export class GroupsState {
    constructor(private groupsService: GroupsService, private store: Store) {}

    @Selector()
    public static getAllGroups(state: GroupsStateModel): IGroup[] {
        return state.groups;
    }

    @Selector()
    public static groupToEdit(state: GroupsStateModel): IGroup {
        return state.groupToEdit;
    }

    @Selector()
    public static groupsChoices(state: GroupsStateModel): IQuestionChoices[] {
        return state.groups.map((group: IGroup) => ({
            value: group.id,
            label: group.name,
        }));
    }

    // READ
    @Action(GetAllGroups)
    public getAllGroups(ctx: StateContext<GroupsStateModel>, action: GetAllGroups): Observable<IGroup[]> | null {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        if ((connectedUser.isAdmin || ctx.getState().groups.length) && !action.forceCall) {
            return null;
        }
        return this.groupsService.getAllGroups(selectedUser).pipe(
            tap((result: IGroup[]) => {
                if (result) {
                    ctx.patchState({
                        groups: result,
                    });
                    ctx.dispatch(new SetRepertoire());
                }
            })
        );
    }

    @Action(GetGroupById)
    public setGroupToEdit(ctx: StateContext<GroupsStateModel>, action: GetGroupById): Observable<IGroup> | void {
        const state: GroupsStateModel = ctx.getState();
        const groupFound: IGroup = state.groups.find((group: IGroup) => group.id === action.id);
        if (!state.groups.length && !groupFound) {
            return this.groupsService.getGroupById(action.id).pipe(
                tap((result: IGroup) => {
                    if (result) {
                        ctx.patchState({
                            groupToEdit: result,
                        });
                    }
                })
            );
        } else {
            ctx.patchState({
                groupToEdit: groupFound,
            });
        }
    }

    // CREATE
    @Action(CreateNewGroup)
    public createNewGroup(ctx: StateContext<GroupsStateModel>, action: CreateNewGroup): Observable<IGroup> {
        return this.groupsService.createGroup(action.newGroup).pipe(
            tap((result: IGroup) => {
                ctx.setState(
                    patch({
                        groupToEdit: null,
                        groups: append([result]),
                    })
                );
            })
        );
    }

    // UPDATE
    @Action(PatchGroup)
    public updateGroup(ctx: StateContext<GroupsStateModel>, action: PatchGroup): Observable<IGroup> {
        const groupIdToUpdate: number = action.group.group.id;
        return this.groupsService.patchGroup(action.group, groupIdToUpdate).pipe(
            tap((result: IGroup) => {
                ctx.setState(
                    patch({
                        groups: updateItem<IGroup>((entity: IGroup) => entity.id === groupIdToUpdate, result),
                    })
                );
            })
        );
    }

    // DELETE
    @Action(DeleteGroup)
    public deleteSchool(ctx: StateContext<GroupsStateModel>, action: DeleteGroup): Observable<void> {
        return this.groupsService.deleteGroup(action.groupId).pipe(
            tap(() => {
                ctx.patchState({
                    groups: ctx.getState().groups.filter((group: IGroup) => group.id !== action.groupId),
                });
            })
        );
    }

    // DELETE -> ENABLE & DISABLE
    @Action(EnableGroup)
    public enableGroup(ctx: StateContext<GroupsStateModel>, action: EnableGroup): Observable<IGroup> {
        return this.groupsService.enableGroup(action.groupId).pipe(
            tap((result: IGroup) => {
                ctx.setState(
                    patch({
                        groups: updateItem<IGroup>((school: IGroup) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    @Action(DisableGroup)
    public disableGroup(ctx: StateContext<GroupsStateModel>, action: DisableGroup): Observable<IGroup> {
        return this.groupsService.disableGroup(action.groupId).pipe(
            tap((result: IGroup) => {
                ctx.setState(
                    patch({
                        groups: updateItem<IGroup>((school: IGroup) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    // UTILS
    @Action(DeleteGroupToEdit)
    public deleteGroupToEdit(ctx: StateContext<GroupsStateModel>): void {
        ctx.patchState({
            groupToEdit: null,
        });
    }

    @Action(DeleteCachedGroups)
    public deleteCachedGroups(ctx: StateContext<GroupsStateModel>): void {
        ctx.patchState({
            groups: [],
        });
    }
}
